import { Grid } from "@material-ui/core";
import { useAtomValue, useSetAtom } from "jotai";
import { isUndefined } from "lodash";
import { FC, useCallback, useEffect } from "react";
import { TextField } from "../../../components/forms/text";
import { Resource } from "../../../components/loader/resource";
import { Permission } from "../../../components/permission";
import { Widget } from "../../../components/widget";
import { useAllParticipations } from "../../../store/participations/queries";
import { ParticipationsDTO } from "../../../store/participations/models/dto";
import { useDebouncedInput } from "../../merchants/all/debounced-input.hook";
import { WidgetAdornment } from "./adornment";
import {
  participationPagination,
  selectedKycStatus,
  selectedLaunch,
} from "./atoms";
import { SelectorWrapper, useConfigurationState } from "./configuration-hook";
import { KycStatusSelectorWrapper } from "./kyc-status-hook";
import { PresentationalParticipationList } from "./results";

export type ParticipationsComponentProps = {
  hideName?: boolean;
};

export interface ParticipationsComponentInnerProps
  extends ParticipationsComponentProps {
  email: string;
}

const ParticipationsComponentInner: FC<ParticipationsComponentInnerProps> = ({
  hideName,
  email,
}) => {
  const kycStatus = useAtomValue(selectedKycStatus);
  const launch = useAtomValue(selectedLaunch);
  const setPagination = useSetAtom(participationPagination);

  const pagination = useAtomValue(participationPagination);

  const resource = useAllParticipations(pagination);

  useEffect(() => {
    setPagination({
      filters: [
        {
          field: "emailAddress",
          value: email,
        },
        {
          field: "configurationIds",
          value: launch?.id ? [launch.id] : undefined,
        },
        {
          field: "status",
          value: kycStatus,
        },
      ]
        .filter(({ value }) => value !== "" && !isUndefined(value))
        .map((x) => x as { field: string; value: string }),
      sorting: {
        direction: "desc",
        field: "createdDate",
      },
    });
  }, [setPagination, email, kycStatus, launch]);

  return (
    <Resource r={resource}>
      {useCallback(
        (data: ParticipationsDTO.Responses.GetAll.T) => (
          <PresentationalParticipationList
            participationList={data}
            hideName={hideName}
          />
        ),
        [hideName]
      )}
    </Resource>
  );
};

export const ParticipationsComponent: FC<ParticipationsComponentProps> = (
  props
) => {
  const configurations = useConfigurationState();

  const { debouncedValue, setValue, value } = useDebouncedInput();

  return (
    <Widget
      adornment={<WidgetAdornment allLaunches={configurations.getOrElse([])} />}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            type="text"
            label="E-mail"
          />
        </Grid>
        <Permission roles={["ROLE_ADMIN", "ROLE_MERCHANT"]}>
          <Grid item xs={12} md={3}>
            {configurations.fold(
              () => null,
              (d) => (
                <SelectorWrapper data={d} />
              )
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <KycStatusSelectorWrapper />
          </Grid>
        </Permission>
      </Grid>
      <ParticipationsComponentInner {...props} email={debouncedValue} />
    </Widget>
  );
};
